// Generic confirmation modal.
//
// Used by the affiliate-payout admin pages so every mutation (approve
// / pay / reject / apply) demands an explicit operator click on a
// secondary confirm button after the BE-bound action button is
// pressed. The dialog displays the exact payload the FE is about to
// POST (amount, destination, proposalId, reason) so the operator can
// catch typos before the request leaves the browser.
//
// Tones:
//   * "normal"   — neutral border + accent button.
//   * "warning"  — amber border + amber button (e.g. reject).
//   * "danger"   — red border + red button + "HIGH RISK" label
//                  (e.g. pay = on-chain broadcast).

import { useEffect, useState } from "react";
import {
  ApprovalRequiredChip,
  GAOSAFE_APPROVAL_REASON,
  useWritesBlocked,
} from "../authority";

export type ConfirmDialogTone = "normal" | "warning" | "danger";

/** High-risk type-to-confirm gate. The confirm button stays disabled until
 *  the operator types `expected` exactly (e.g. the human amount "59.70" or
 *  the last 6 chars of the destination). Used only for money-moving actions. */
export interface TypedConfirmation {
  expected: string;
  /** Instruction shown above the input, e.g. 'Type the amount "59.70" to confirm'. */
  label: string;
  placeholder?: string;
}

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** Short description of what is about to happen. */
  description?: React.ReactNode;
  /** Key/value rows of payload data. Rendered as a definition list
   *  so the operator can verify each value before confirming. */
  rows?: ReadonlyArray<{ key: string; value: React.ReactNode }>;
  /** Label on the confirm button. */
  confirmLabel: string;
  cancelLabel?: string;
  tone?: ConfirmDialogTone;
  /** When true, both buttons are disabled and the confirm button
   *  shows a "Submitting…" affordance (POST in-flight). */
  busy?: boolean;
  /** When true, the confirm button is disabled but its label is
   *  preserved (e.g. waiting on a required free-text reason input).
   *  Cancel remains enabled. */
  confirmDisabled?: boolean;
  /** When set, rendered as an inline alert above the buttons. Used
   *  to surface the BE error so the operator can correct and retry
   *  without re-opening the dialog. */
  error?: string | null;
  /** High-risk gate: require the operator to type an exact string before
   *  the confirm button enables. Use for money-moving actions only. */
  requireTypedConfirmation?: TypedConfirmation;
  onConfirm: () => void;
  onCancel: () => void;
}

const TONE_COLOR: Record<ConfirmDialogTone, string> = {
  normal: "var(--accent, #2c7be5)",
  warning: "var(--warn, #b58900)",
  danger: "var(--danger, #d23f3f)",
};

export function ConfirmDialog(props: ConfirmDialogProps) {
  const {
    open,
    title,
    description,
    rows,
    confirmLabel,
    cancelLabel = "Cancel",
    tone = "normal",
    busy = false,
    confirmDisabled = false,
    error = null,
    requireTypedConfirmation,
    onConfirm,
    onCancel,
  } = props;

  const [typed, setTyped] = useState("");

  // The authority gate, applied at the LAST step before any dialog-mediated
  // mutation. Fourteen privileged writes across the money and Control Plane
  // screens funnel through this component, so gating here is what makes the
  // block complete rather than a list of buttons someone has to remember to
  // keep in sync. The per-screen notices explain it; this enforces it.
  //
  // Declared with the other hooks, above the `if (!open) return null` — a
  // hook after an early return changes call order between renders.
  const writesBlocked = useWritesBlocked();

  // Reset the type-to-confirm input each time the dialog opens/closes so a
  // stale match can never carry over between actions.
  useEffect(() => {
    setTyped("");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !busy) onCancel();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const typedOk =
    !requireTypedConfirmation || typed.trim() === requireTypedConfirmation.expected;
  const confirmBlocked = writesBlocked || confirmDisabled || !typedOk;

  const accent = TONE_COLOR[tone];
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 16,
      }}
    >
      <div
        style={{
          background: "var(--bg-card, #fff)",
          border: `2px solid ${accent}`,
          borderRadius: 6,
          padding: "16px 18px",
          maxWidth: 560,
          width: "100%",
          maxHeight: "85vh",
          overflowY: "auto",
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
        }}
      >
        <h3 style={{ margin: "0 0 8px 0", fontSize: 15, color: accent }}>
          {tone === "danger" ? "⚠ HIGH RISK · " : ""}
          {title}
        </h3>
        {description && (
          <div style={{ fontSize: 13, color: "var(--fg)", margin: "8px 0" }}>
            {description}
          </div>
        )}
        {rows && rows.length > 0 && (
          <dl
            style={{
              margin: "12px 0",
              padding: "10px 12px",
              border: "1px solid var(--border, #ccc)",
              borderRadius: 4,
              background: "var(--bg-faint, #fafafa)",
              display: "grid",
              gridTemplateColumns: "max-content 1fr",
              gap: "6px 12px",
              fontSize: 12,
            }}
          >
            {rows.map((r) => (
              <div
                key={r.key}
                style={{ display: "contents" }}
              >
                <dt style={{ color: "var(--fg-dim, #666)" }}>{r.key}</dt>
                <dd style={{ margin: 0, wordBreak: "break-all" }}>{r.value}</dd>
              </div>
            ))}
          </dl>
        )}
        {requireTypedConfirmation && (
          <div style={{ margin: "12px 0" }}>
            <label
              htmlFor="confirm-typed"
              style={{ display: "block", fontSize: 12.5, color: "var(--fg)", marginBottom: 4 }}
            >
              {requireTypedConfirmation.label}
            </label>
            <input
              id="confirm-typed"
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={requireTypedConfirmation.placeholder ?? requireTypedConfirmation.expected}
              autoComplete="off"
              aria-invalid={!typedOk}
              disabled={busy}
              style={{
                width: "100%",
                padding: "6px 8px",
                fontFamily: "monospace",
                fontSize: 13,
                border: typedOk
                  ? "1px solid var(--ok, #0a9444)"
                  : "1px solid var(--border, #ccc)",
              }}
            />
          </div>
        )}
        {error && (
          <p
            role="alert"
            style={{
              margin: "8px 0",
              padding: 10,
              border: "1px solid var(--danger, #d23f3f)",
              background: "rgba(210,63,63,0.06)",
              color: "var(--danger, #d23f3f)",
              borderRadius: 4,
              fontSize: 12,
              whiteSpace: "pre-wrap",
            }}
          >
            {error}
          </p>
        )}
        <div
          style={{
            display: "flex",
            gap: 8,
            marginTop: 12,
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          {writesBlocked && (
            <span style={{ marginRight: "auto" }}>
              <ApprovalRequiredChip />
            </span>
          )}
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            style={{ padding: "6px 14px", fontSize: 13 }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={writesBlocked ? undefined : onConfirm}
            title={writesBlocked ? GAOSAFE_APPROVAL_REASON : undefined}
            disabled={busy || confirmBlocked}
            style={{
              padding: "6px 14px",
              fontSize: 13,
              background: accent,
              color: "white",
              border: "none",
              borderRadius: 3,
              cursor: busy || confirmBlocked ? "default" : "pointer",
              fontWeight: 600,
              opacity: confirmBlocked && !busy ? 0.6 : 1,
            }}
          >
            {busy ? "Submitting…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
